// src/components/App/App.js

import React, { Suspense, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { Routes, Route, Navigate, Outlet, useSearchParams } from "react-router-dom"; // Add useSearchParams
import Header from "../Header/Header";
import PersonalAccessTokensContainer from "../PersonalAccessTokens/PersonalAccessTokensContainer";
import PassiveSearchContainer from "../PassiveSearch/PassiveSearchContainer";
import { NotFound } from "../NotFound";
import TourRunner from "../OnboardingTour/TourRunner";
import VerificationGuard from "./Requirements/VerificationGuard";
import RouteGuard from "./Requirements/RouteGuard";
import SignUpPage from "../Authentication/SignUpForm/SignUpPage";
import SignInPage from "../Authentication/SignInForm/SignInPage";
import Footer from "../Footer/Footer";
import { useTour } from "../../shared/TourContext";
import FullScreenLoader from "../Common/FullScreenLoader";
import GoogleAuthSuccessHandler from './GoogleAuthSuccessHandler';
import GoogleAuthConfirmHandler from './GoogleAuthConfirmHandler';
import { PatchNotesModal, PatchNotesPage } from '../PatchNotes/PatchNotes';

const IdealCandidatesListingContainer = React.lazy(() => import('../IdealCandidatesListing/IdealCandidatesListingContainer'));
const IdealCandidateViewContainer = React.lazy(() => import('../IdealCandidateView/IdealCandidateViewContainer'));
const SettingsPage = React.lazy(() => import('../Settings'));
const HelpPage = React.lazy(() => import('../Help/HelpPage'));

const AppLayout = ({ user, signOut, showFooter, onWhatsNewClick }) => (
  <VerificationGuard>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <TourRunner />
      <Header user={user} signOut={signOut} onWhatsNewClick={onWhatsNewClick} />
      <Box sx={{ flexGrow: 1, p: { xs: 2, sm: 3, md: 4 } }}>
        <Outlet />
      </Box>
      <Footer showFooter={showFooter}/>
    </Box>
  </VerificationGuard>
);

// New Component: Decides whether to show the Dashboard or the Success Handler
const DashboardOrSuccess = () => {
  const [searchParams] = useSearchParams();
  // Check for the flag we added OR if tokens are present directly on root
  const isAuthRedirect = searchParams.get('oauth_redirect') || searchParams.get('token') || searchParams.get('code');

  if (isAuthRedirect) {
    return <GoogleAuthSuccessHandler />;
  }

  return (
    <RouteGuard>
      <IdealCandidatesListingContainer />
    </RouteGuard>
  );
};

export default function App({ user, signOut, isLoading }) {
  const isAuth = !!user;
  const { setRunTour } = useTour();
  const [showFooter, setShowFooter] = useState(false);
  const [isPatchNotesModalOpen, setIsPatchNotesModalOpen] = useState(false);


  useEffect(() => {
    const hasCompletedTour = localStorage.getItem('hasCompletedOnboardingTour');
    if (user && !hasCompletedTour) {
      setRunTour(true);
      localStorage.setItem('hasCompletedOnboardingTour', 'true');
    }
  }, [user, setRunTour]);

  useEffect(() => {
    const handleScroll = () => {
      const isBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 100; 
      setShowFooter(isBottom);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isLoading) {
    return <FullScreenLoader />;
    
  }

  return (
    <Suspense fallback={<FullScreenLoader />}>
        <PatchNotesModal
            open={isPatchNotesModalOpen}
            onClose={() => setIsPatchNotesModalOpen(false)}
        />
      <Routes>
        {/* Keep for backward compatibility if needed, but we rely on root now */}
        <Route path="/calendar/success" element={<GoogleAuthSuccessHandler />} />
        <Route path="/calendar/confirm" element={<GoogleAuthConfirmHandler />} />
        
        {isAuth ? (
          <>
            <Route element={<AppLayout user={user} signOut={signOut} showFooter={showFooter} onWhatsNewClick={() => setIsPatchNotesModalOpen(true)} />}>
                <Route path="/patch-notes" element={<PatchNotesPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                
                {/* Updated Root Route to handle OAuth callback */}
                <Route path="/" element={<DashboardOrSuccess />} />
                
                <Route path="/ideal-candidates" element={<RouteGuard><IdealCandidatesListingContainer /></RouteGuard>} />
                <Route path="/ideal-candidates/:idealCandidateUuid" element={<RouteGuard><IdealCandidateViewContainer /></RouteGuard>} />
                <Route path="/pats" element={<RouteGuard><PersonalAccessTokensContainer /></RouteGuard>} />
                <Route path="/passive-search" element={<RouteGuard requireTiers={["basic" ,"pro", "enterprise"]}><PassiveSearchContainer /></RouteGuard>} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="*" element={<NotFound />} />
              </Route>
          </>
        ) : (
          <>
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/signin" element={<SignInPage />} />
            <Route path="*" element={<Navigate to="/signin" replace />} />
          </>
        )}
      </Routes>
    </Suspense>
  );
}